import { useEffect, useMemo, useRef, useState } from "react";
import { X } from "../../components/icons/X";
import { Button as ButtonV2 } from "../../components/ButtonV2/Button";
import { Input, PhoneInput } from "../../components/Input/Input";
import { CampaignTracking } from "../../utils/CampaignTracking";
import { getOrCreateVisitorId } from "../../utils/visitorId";
import { LoadingButton } from "../../components/icons/LoadingButton";
import { formatGtagLabel } from "../../utils/formatGtagLabel";
import { useAntiBot, ANTIBOT_ACTIONS } from "../../hooks/useAntiBot";
import { useSubmitGuard } from "../../hooks/useSubmitGuard";
import {
  captureLeadAttempt,
  createCaptureId,
  outcomeForStatus,
  reportCaptureOutcome,
} from "../../utils/leadCapture";
import { AntiBotNotice } from "../../components/AntiBotNotice";
import {
  openExternalTab,
  reserveExternalTab,
  type ReservedExternalTab,
} from "../../utils/externalTab";
import { isSafariUserAgent } from "../../utils/userAgent";
import type {
  ContactFormData,
  ContactFormModalProps,
  ContactFormSubmitPayload,
  ContactOption,
  ContactPreference,
} from "./ContactFormModal.types";
import {
  CONTACT_FIELD_VALIDATORS,
  REQUIRED_FIELD_MESSAGE,
} from "./ContactFormModal.constants";
import {
  Actions,
  CheckboxLabel,
  ConsentWrapper,
  ContentSelect,
  FieldGroup,
  FieldLabel,
  Form,
  Header,
  ModalContainer,
  Overlay,
  PreferenceGrid,
  Title,
  CloseButton,
  FieldErrorMessage,
  SubmitRow,
  LoadingIndicator,
  FormMessage,
  FormMessageContainer,
  FormMessageTitle,
} from "./ContactFormModal.styles";

const CAPTURE_SOURCE = "ContactFormModal";

const defaultContactOptions: ContactOption[] = [
  { id: "whatsapping", label: "Whatsapp" },
  { id: "phoning", label: "Telefone" },
  { id: "mailing", label: "E-mail" },
];

const fieldValidators = CONTACT_FIELD_VALIDATORS;

export const ContactFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  submitUrl,
  antiBot,
  accountId,
  siteId,
  product,
  teamId,
  unitId,
  unitOptions,
  moduleId,
  moduleName,
  bait,
  contentSelect,
  resolveRedirectUrl,
  onError,
  successMessage,
  errorMessage,
  className,
  title = "Fale por WhatsApp",
  submitLabel = "iniciar conversa",
  defaultValues,
  contactOptions,
  consentLabel = "Autorizo o uso de minhas informações pessoais para campanhas de marketing.",
  afterRedirect,
}: ContactFormModalProps) => {
  const options = useMemo(
    () => contactOptions ?? defaultContactOptions,
    [contactOptions],
  );

  const formRef = useRef<HTMLFormElement>(null);
  const [formSent, setFormSent] = useState(false);
  const { isSubmitting, runExclusive } = useSubmitGuard();
  const [serverError, setServerError] = useState(false);
  const [fieldsWithError, setFieldsWithError] = useState<string[]>([]);
  const [contentEntryId, setContentEntryId] = useState(
    contentSelect?.preselectedId ?? "",
  );
  const [selectedUnitId, setSelectedUnitId] = useState("");

  const { execute: executeAntiBot, requestScript: requestAntiBotScript } = useAntiBot(antiBot);

  useEffect(() => {
    if (isOpen) {
      setFormSent(false);
      setServerError(false);
      setFieldsWithError([]);

      if (formRef.current && defaultValues) {
        const form = formRef.current;
        if (defaultValues.name)
          (form.elements.namedItem("name") as HTMLInputElement).value =
            defaultValues.name;
        if (defaultValues.email)
          (form.elements.namedItem("email") as HTMLInputElement).value =
            defaultValues.email;
        if (defaultValues.phone)
          (form.elements.namedItem("phone") as HTMLInputElement).value =
            defaultValues.phone;
        if (defaultValues.marketingOptIn !== undefined) {
          (
            form.elements.namedItem("dataPermissions") as HTMLInputElement
          ).checked = defaultValues.marketingOptIn;
        }
        if (defaultValues.preferences) {
          defaultValues.preferences.forEach((pref) => {
            const checkbox = form.elements.namedItem(pref) as HTMLInputElement;
            if (checkbox) checkbox.checked = true;
          });
        }
      }
    }
  }, [defaultValues, isOpen]);

  useEffect(() => {
    setContentEntryId(contentSelect?.preselectedId ?? "");
  }, [contentSelect?.preselectedId]);

  if (!isOpen) {
    return null;
  }

  const offeredUnits = unitId ? [] : (unitOptions ?? []);
  const shouldAskUnit = offeredUnits.length > 1;
  const soleUnit = offeredUnits.length === 1 ? offeredUnits[0] : undefined;

  const validateForm = (data: { [key: string]: any }) => {
    const fieldsWithError: string[] = [];
    let isFormValid = true;

    (
      Object.keys(fieldValidators) as Array<keyof typeof fieldValidators>
    ).forEach((field) => {
      if (!fieldValidators[field](data[field] || "")) {
        fieldsWithError.push(field);
        isFormValid = false;
      }
    });

    const contactIsValid =
      data["whatsapping"] === "on" ||
      data["phoning"] === "on" ||
      data["mailing"] === "on";

    if (!contactIsValid) {
      fieldsWithError.push("preferences");
      isFormValid = false;
    }

    if (shouldAskUnit && !data["unit"]) {
      fieldsWithError.push("unit");
      isFormValid = false;
    }

    setFieldsWithError(fieldsWithError);
    return isFormValid;
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (
    event,
  ) => {
    event.preventDefault();

    const form = event.currentTarget as HTMLFormElement;
    const formData = new FormData(form);
    const data = Object.fromEntries(formData);
    const captureId = createCaptureId();

    captureLeadAttempt({
      captureId,
      source: CAPTURE_SOURCE,
      rawPayload: data,
      pageUrl: typeof window !== "undefined" ? window.location.href : undefined,
      formId: bait?.id,
      visitorId: getOrCreateVisitorId() ?? undefined,
    });

    if (!validateForm(data)) {
      reportCaptureOutcome({
        captureId,
        outcome: "REJECTED_CLIENT_VALIDATION",
      });
      return;
    }

    await runExclusive({
      task: async () => {
        const reservedTab: ReservedExternalTab | null =
          resolveRedirectUrl && isSafariUserAgent()
            ? reserveExternalTab({ placeholderMessage: "Abrindo o WhatsApp..." })
            : null;

        const campaignTracking = CampaignTracking.getTrackingData();

        const selectedContentEntry = contentSelect?.options.find(
          (option) => option.id === data.content_entry_id,
        );

        const selectedUnit =
          soleUnit ?? offeredUnits.find((option) => option.id === data.unit);

        const afVisitorId = getOrCreateVisitorId();
        const payload: ContactFormSubmitPayload = {
          name: data.name as string,
          email: data.email as string,
          phone: data.phone as string,
          preferences: [
            ...(data.whatsapping === "on"
              ? ["whatsapping" as ContactPreference]
              : []),
            ...(data.phoning === "on" ? ["phoning" as ContactPreference] : []),
            ...(data.mailing === "on" ? ["mailing" as ContactPreference] : []),
          ],
          marketingOptIn: data.dataPermissions === "on",
          whatsapping: data.whatsapping === "on",
          phoning: data.phoning === "on",
          mailing: data.mailing === "on",
          ...(afVisitorId ? { af_visitor_id: afVisitorId } : {}),
          ...(Object.keys(campaignTracking).length > 0
            ? { campaign: campaignTracking }
            : {}),
          ...(moduleId ? { module_id: moduleId } : {}),
          ...(moduleId && moduleName ? { module: moduleName } : {}),
          ...(selectedContentEntry
            ? { content_entry_id: selectedContentEntry.id }
            : {}),
          ...(selectedUnit ? { unit_name: selectedUnit.label } : {}),
          ...(bait ? { bait } : {}),
        };

        const deliverRedirect = () => {
          const redirectUrl = resolveRedirectUrl?.(payload) ?? null;

          if (!redirectUrl) {
            reservedTab?.cancel();
            return;
          }

          if (reservedTab) reservedTab.navigate(redirectUrl);
          else openExternalTab({ url: redirectUrl });
        };

        const crmSubmitUrl = process.env.NEXT_PUBLIC_DEFAULT_FORM_SUBMIT_URL;
        if (crmSubmitUrl && accountId && siteId) {
          const antiBotToken = await executeAntiBot(
            ANTIBOT_ACTIONS.WHATSAPP_CONTACT,
          );

          try {
            const crmResponse = await fetch(crmSubmitUrl, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                ...payload,
                af_capture_id: captureId,
                accountId,
                siteId,
                channel: "Site",
                product: selectedContentEntry?.label || product || "WhatsApp",
                ...(typeof window !== "undefined"
                  ? { origin_url: window.location.href }
                  : {}),
                ...(teamId ? { teamId } : {}),
                ...(unitId
                  ? { unitId }
                  : selectedUnit
                    ? { unitId: selectedUnit.id }
                    : {}),
                ...(antiBotToken ? { antiBotToken } : {}),
              }),
            });

            const crmAccepted =
              crmResponse.status >= 200 && crmResponse.status <= 299;

            if (crmAccepted) {
              reportCaptureOutcome({ captureId, outcome: "ACCEPTED_QUEUED" });
            } else {
              reportCaptureOutcome({
                captureId,
                ...outcomeForStatus(crmResponse.status),
              });
            }

            if (crmResponse.status === 403) {
              reservedTab?.cancel();
              setServerError(true);
              return;
            }
          } catch (error) {
            reportCaptureOutcome({
              captureId,
              outcome: "TRANSPORT_FAILED",
              outcomeDetail: error instanceof Error ? error.message : undefined,
            });
          }
        }

        try {
          if (submitUrl) {
            const { status } = await fetch(submitUrl, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(payload),
            });

            if (status < 200 || status > 299) {
              reservedTab?.cancel();
              setServerError(true);
              return;
            }
          }

          if (afterRedirect?.active) {
            reservedTab?.cancel();
            return (window.location.href = afterRedirect.url || "");
          }

          deliverRedirect();
          form.dispatchEvent(new CustomEvent("conversion", { detail: payload }));
          onSubmit?.(payload);
          setFormSent(true);
        } catch (error) {
          if (onSubmit) {
            try {
              const submitResult = await onSubmit(payload);

              if (submitResult === false) {
                reservedTab?.cancel();
                setServerError(true);
                onError?.(new Error("Falha ao enviar formulário de contato."));
              } else {
                if (afterRedirect?.active) {
                  reservedTab?.cancel();
                  return (window.location.href = afterRedirect.url || "");
                }
                deliverRedirect();
                form.dispatchEvent(
                  new CustomEvent("conversion", { detail: payload }),
                );
                setFormSent(true);
              }
            } catch (callbackError) {
              reservedTab?.cancel();
              onError?.(callbackError);
              setServerError(true);
            }
          } else {
            reservedTab?.cancel();
            onError?.(error);
            setServerError(true);
          }
        }
      },
    });
  };

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const buttonLabel = submitLabel.toUpperCase();

  return (
    <Overlay
      className={className}
      onClick={handleOverlayClick}
    >
      <ModalContainer>
        <Header>
          <Title>{title}</Title>

          <CloseButton
            type="button"
            aria-label="Fechar"
            onClick={onClose}
          >
            <X
              color="#0F172A"
              size={16}
            />
          </CloseButton>
        </Header>

        {!formSent && (
          <Form
            ref={formRef}
            onSubmit={handleSubmit}
            onFocus={requestAntiBotScript}
            noValidate
            data-gtag-action="Formulário Enviado"
            data-gtag-category="Formulário"
            data-gtag-label={formatGtagLabel(
              "contact-form-modal",
              title,
              submitLabel,
            )}
          >
            <Input
              label="NOME"
              placeholder="Digite seu nome completo"
              name="name"
              error={fieldsWithError.includes("name")}
              helperText={
                fieldsWithError.includes("name") ? REQUIRED_FIELD_MESSAGE : ""
              }
              onChange={() =>
                setFieldsWithError((prev) =>
                  prev.filter((field) => field !== "name"),
                )
              }
            />

            <Input
              type="email"
              label="E-MAIL"
              placeholder="Ex: joaosilva@empresa.com"
              name="email"
              error={fieldsWithError.includes("email")}
              helperText={
                fieldsWithError.includes("email")
                  ? "Por favor, preencha esse campo"
                  : ""
              }
              onChange={() =>
                setFieldsWithError((prev) =>
                  prev.filter((field) => field !== "email"),
                )
              }
            />

            <PhoneInput
              label="TELEFONE"
              placeholder="Digite seu telefone com DDD"
              name="phone"
              error={fieldsWithError.includes("phone")}
              helperText={
                fieldsWithError.includes("phone")
                  ? "Por favor, preencha esse campo"
                  : ""
              }
              onChange={() =>
                setFieldsWithError((prev) =>
                  prev.filter((field) => field !== "phone"),
                )
              }
            />

            {contentSelect && contentSelect.options.length > 0 && (
              <ContentSelect
                label={contentSelect.label}
                name="content_entry_id"
                $isPlaceholder={contentEntryId === ""}
                value={contentEntryId}
                onChange={(event) => setContentEntryId(event.target.value)}
                options={[
                  {
                    value: "",
                    label: contentSelect.placeholder ?? "Selecione",
                  },
                  ...contentSelect.options.map((option) => ({
                    value: option.id,
                    label: option.label,
                  })),
                ]}
              />
            )}

            {shouldAskUnit && (
              <ContentSelect
                label="UNIDADE"
                name="unit"
                $isPlaceholder={selectedUnitId === ""}
                value={selectedUnitId}
                error={fieldsWithError.includes("unit")}
                helperText={
                  fieldsWithError.includes("unit") ? REQUIRED_FIELD_MESSAGE : ""
                }
                onChange={(event) => {
                  setSelectedUnitId(event.target.value);
                  setFieldsWithError((prev) =>
                    prev.filter((field) => field !== "unit"),
                  );
                }}
                options={[
                  { value: "", label: "Selecione" },
                  ...offeredUnits.map((option) => ({
                    value: option.id,
                    label: option.label,
                  })),
                ]}
              />
            )}

            <FieldGroup>
              <FieldLabel>PREFERÊNCIA DE CONTATO</FieldLabel>
              <PreferenceGrid>
                {options.map((option) => (
                  <CheckboxLabel
                    key={option.id}
                    $highlight
                  >
                    <input
                      type="checkbox"
                      name={option.id}
                      onChange={() =>
                        setFieldsWithError((prev) =>
                          prev.filter((field) => field !== "preferences"),
                        )
                      }
                    />
                    {option.label}
                  </CheckboxLabel>
                ))}
              </PreferenceGrid>
              {fieldsWithError.includes("preferences") && (
                <FieldErrorMessage role="alert">
                  {REQUIRED_FIELD_MESSAGE}
                </FieldErrorMessage>
              )}
            </FieldGroup>

            <ConsentWrapper>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  name="dataPermissions"
                />
                {consentLabel}
              </CheckboxLabel>
            </ConsentWrapper>

            {serverError && (
              <FieldErrorMessage style={{ textAlign: "center" }}>
                {errorMessage ||
                  "Ocorreu um erro no servidor. Tente novamente mais tarde."}
              </FieldErrorMessage>
            )}

            <Actions>
              <SubmitRow className={isSubmitting ? "is-loading" : undefined}>
                <ButtonV2
                  className="styleguide"
                  size="full"
                  type="submit"
                  palette={{
                    bgColor: "#22c55e",
                    borderColor: "#22c55e",
                    textColor: "#ffffff",
                  }}
                  disabled={isSubmitting}
                  aria-label={buttonLabel}
                >
                  {isSubmitting ? "ENVIANDO..." : buttonLabel}
                </ButtonV2>
                {isSubmitting && (
                  <LoadingIndicator
                    role="status"
                    aria-live="polite"
                    aria-label="Enviando"
                  >
                    <LoadingButton
                      className="loading-icon"
                      color="#ffffff"
                    />
                  </LoadingIndicator>
                )}
              </SubmitRow>
              <AntiBotNotice antiBot={antiBot} />
            </Actions>
          </Form>
        )}

        {formSent && (
          <FormMessage>
            <FormMessageContainer>
              <FormMessageTitle>
                {successMessage || "Mensagem enviada com sucesso!"}
              </FormMessageTitle>
            </FormMessageContainer>
          </FormMessage>
        )}
      </ModalContainer>
    </Overlay>
  );
};

export type {
  ContactFormModalProps,
  ContactFormSubmitPayload,
  ContactFormData,
  ContactPreference,
  ContactOption,
} from "./ContactFormModal.types";
